import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { API, ax, errorText } from "./common";

export default function MyRouteTab({ showMsg, refreshTick = 0 }) {
  const { t } = useLang();
  const [profiles, setProfiles] = useState([]);
  const [notes, setNotes] = useState([]);

  // Separate requests, so a failing notification call never hides the route profiles.
  const load = useCallback(async (silent = false) => {
    try {
      setProfiles((await axios.get(`${API}/route-profiles`, ax)).data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
    try {
      const { data } = await axios.get(`${API}/notifications`, ax);
      setNotes(data.items.filter(n => n.type.startsWith("ROUTE_PROFILE_")));
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, ax);
      load();
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  };

  const place = (w) => (w?.name ? `${w.name} (${w.country})` : "-");

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className="text-sm font-bold text-slate-800">{t("adm.rp.title")}</h3>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
          {profiles.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.rp.empty")}</p>}
          {profiles.map(p => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm truncate">
                  {p.name} <span className="font-mono text-xs text-slate-400">{p.code} · v{p.version}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {place(p.source_warehouse)} → {place(p.destination_warehouse)} · {t(`adm.marketplace.${p.marketplace}`)}
                </p>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-lg ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {t(p.active ? "adm.active" : "adm.inactive")}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-bold text-slate-800">{t("adm.nt.title")}</h3>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
          {notes.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.nt.empty")}</p>}
          {notes.map(n => (
            <div key={n.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${n.read_at ? "text-slate-500" : "font-semibold text-slate-900"}`}>
                  {t(`adm.nt.${n.type}`, { code: n.data?.code ?? "", version: n.data?.version ?? "" })}
                </p>
                <p className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
              </div>
              {!n.read_at && (
                <Button variant="outline" size="sm" onClick={() => markRead(n.id)}>{t("adm.nt.mark_read")}</Button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
